import { useSeo } from "../lib/useSeo";

const getSiteDomain = () => {
    if (typeof window === "undefined") return "this site";
    return window.location.host;
};

export const PrivacyPage = () => {
    useSeo({
        title: "Privacy Policy",
        description:
            "Privacy policy for our hydration calculator site. We do not collect personal data. Cookies from Google AdSense and analytics, GDPR rights, contact details.",
        path: "/privacy-policy",
    });

    const domain = getSiteDomain();
    const privacyEmail = `privacy@${domain}`;

    return (
        <section className="py-12 sm:py-20" data-testid="privacy-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="editorial-label mb-3">Privacy</div>
                <h1 className="text-5xl sm:text-7xl font-black tracking-tighter uppercase leading-none mb-6">
                    Privacy Policy.
                </h1>
                <p className="text-sm text-foreground/60 mb-12">
                    Last updated: February 1, 2026
                </p>

                <div className="space-y-8 text-base text-foreground/85 leading-relaxed">
                    <p>
                        This Privacy Policy describes how Hydration Hub ("we", "us", "our")
                        collects, uses, and protects information when you visit
                        <strong> {domain}</strong> (the "Site"). This policy is intended
                        to comply with the EU General Data Protection Regulation (GDPR), the
                        UK GDPR, and the California Consumer Privacy Act (CCPA).
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        1. Information we collect
                    </h2>
                    <p>
                        Hydration Hub does not collect personal data through forms, accounts
                        or sign-ups. There are no user accounts on the Site.
                    </p>
                    <p>
                        Calculator inputs (weight, age, activity level) are processed entirely
                        in your browser. They are <strong>never transmitted</strong> to a
                        server and we never see them.
                    </p>
                    <p>
                        We store your unit preference (metric or imperial) in your browser's
                        localStorage so the Site remembers it on return visits. This data
                        never leaves your device.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        2. Cookies and similar technologies
                    </h2>
                    <p>
                        We do not set first-party tracking cookies. However, third parties
                        listed in the section below may set cookies and use similar
                        technologies under their own privacy policies.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        3. Google AdSense and third-party advertising
                    </h2>
                    <p>
                        Hydration Hub displays ads served by Google AdSense. Google, as a
                        third-party vendor, uses cookies to serve ads on this Site.
                    </p>
                    <ul className="space-y-2 list-disc pl-6">
                        <li>
                            Google's use of advertising cookies enables it and its partners to
                            serve ads to users based on their visits to this Site and other sites
                            on the internet.
                        </li>
                        <li>
                            Users may opt out of personalized advertising by visiting{" "}
                            <a
                                href="https://www.google.com/settings/ads"
                                className="text-primary underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Google Ads Settings
                            </a>
                            .
                        </li>
                        <li>
                            Alternatively, users can opt out of a third-party vendor's use of
                            cookies for personalized advertising by visiting{" "}
                            <a
                                href="https://www.aboutads.info/"
                                className="text-primary underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                aboutads.info
                            </a>
                            .
                        </li>
                    </ul>
                    <p>
                        For users in the European Economic Area (EEA), the United Kingdom, or
                        Switzerland, Google will use only non-personalized advertising
                        cookies unless explicit consent is provided.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        4. Analytics
                    </h2>
                    <p>
                        We may use privacy-respecting analytics (such as aggregated traffic
                        counts) to understand how visitors use the Site. No personal data
                        identifying individual visitors is collected or stored.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        5. Affiliate links
                    </h2>
                    <p>
                        Some outbound links on the Site may be affiliate links. If you click
                        such a link and make a purchase, we may receive a small commission at
                        no additional cost to you. Affiliate networks may set their own
                        cookies. We only recommend products we believe are worth recommending.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        6. Your rights under GDPR / UK GDPR
                    </h2>
                    <p>If you are in the EEA, UK, or Switzerland, you have the right to:</p>
                    <ul className="space-y-2 list-disc pl-6">
                        <li>Access any personal data we may hold about you (we hold none).</li>
                        <li>Request deletion of any personal data.</li>
                        <li>Object to or restrict processing.</li>
                        <li>Data portability.</li>
                        <li>Withdraw consent for advertising cookies at any time.</li>
                        <li>Lodge a complaint with your local supervisory authority.</li>
                    </ul>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        7. Your rights under CCPA (California)
                    </h2>
                    <p>
                        California residents have the right to know what personal data is
                        collected, request deletion, and opt out of the "sale" of personal
                        information. Hydration Hub does not sell personal information.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        8. Children's privacy
                    </h2>
                    <p>
                        The Site is not directed to children under 13 (or the equivalent
                        minimum age in the relevant jurisdiction). We do not knowingly
                        collect data from children.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        9. Data retention and security
                    </h2>
                    <p>
                        Because we do not collect personal data on our servers, there is
                        nothing to retain or breach on our end. Browser-stored preferences
                        (unit preference) remain only on your device and can be cleared at
                        any time via your browser settings.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        10. Changes to this policy
                    </h2>
                    <p>
                        We may update this Privacy Policy from time to time. The "Last
                        updated" date at the top of this page reflects the latest revision.
                    </p>

                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-12 mb-3">
                        11. Contact
                    </h2>
                    <p>
                        Questions about this policy? Email{" "}
                        <a
                            href={`mailto:${privacyEmail}`}
                            className="text-primary underline"
                            data-testid="privacy-contact-email"
                        >
                            {privacyEmail}
                        </a>
                        .
                    </p>
                </div>
            </div>
        </section>
    );
};
